import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source';
import { supabase } from '../supabaseClient'; // Correctly import the supabase client

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

// Helper to get the auth token securely
export const getAuthToken = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
        console.error('Error getting Supabase session:', error);
        return null;
    }
    if (!session) {
        console.warn('No active Supabase session found.');
        return null;
    }
    return session.access_token;
};

/**
 * Shared authenticated JSON fetch.
 *
 * On a 401 it forces one Supabase session refresh and retries, so a stale
 * access token doesn't surface as a generic "Request failed: 401". Throws with
 * the backend `detail` on any other non-OK response.
 */
export const authFetch = async (
    url: string,
    options: RequestInit = {},
): Promise<Response> => {
    const attempt = async (): Promise<Response> => {
        const token = await getAuthToken();
        if (!token) throw new Error('Not authenticated');
        return fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...(options.headers as Record<string, string> | undefined),
            },
        });
    };

    let response = await attempt();
    if (response.status === 401) {
        const { error } = await supabase.auth.refreshSession();
        if (!error) response = await attempt();
    }

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Request failed: ${response.status}`);
    }
    return response;
};

// --- Chat API ---
export const streamChat = async (
    chatId: string,
    userText: string,
    userHtml: string,
    userTimezone: string,
    userTimestamp: string,
    signal: AbortSignal,
    onMessage: (event: EventSourceMessage) => void,
    onError: (error: any) => void,
    onClose: () => void
) => {
    const token = await getAuthToken();
    if (!token) {
        onError(new Error("Authentication token not found. Please log in."));
        return;
    }

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };

    try {
        await fetchEventSource(`${API_BASE_URL}/chat/${chatId}`, {
            method: 'POST',
            headers: headers,
            openWhenHidden: true,
            body: JSON.stringify({
                user_text: userText,
                user_html: userHtml,
                chat_id: chatId,
                user_timezone: userTimezone,
                user_timestamp: userTimestamp,
            }),
            signal: signal,
            onopen: async (response) => {
                if (!response.ok) {
                    const errorPayload = {
                        status: response.status,
                        statusText: response.statusText,
                        message: `Failed to connect to chat stream. Server responded with status ${response.status}.`
                    };
                    onError(errorPayload);
                    throw new Error(JSON.stringify(errorPayload)); // Stop further processing
                }
            },
            onmessage: onMessage,
            onerror: (err) => {
                onError(err);
                throw err; // stop the library's infinite retry loop
            },
            onclose: onClose,
        });
    } catch {
        // onError already surfaced the failure.
    }
};


// --- Gmail API ---
export const connectGmail = async () => {
    const token = await getAuthToken();
    if (!token) {
        alert('Authentication token not found. Please log in.');
        throw new Error('Authentication token not found.');
    }

    // The backend will respond with a redirect URL.
    const response = await fetch(`${API_BASE_URL}/auth/google/login`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });

    if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Failed to initiate Gmail connection.';
        alert(`Error: ${errorMessage}`);
        throw new Error(errorMessage);
    }

    const data = await response.json();
    if (data.authorization_url) {
        // Redirect the user to the Google auth page
        window.location.href = data.authorization_url;
    } else {
        const msg = 'Could not get authorization URL from server.';
        alert(msg);
        throw new Error(msg);
    }
};

export const listGmailMessages = async () => {
    const token = await getAuthToken();
    if (!token) {
        alert('Authentication token not found. Please log in.');
        throw new Error('Authentication token not found.');
    }

    const response = await fetch(`${API_BASE_URL}/gmail/messages`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });

    if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Failed to list Gmail messages.';
        alert(`Error: ${errorMessage}`);
        throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('Emails listed successfully:', data);
    alert(`Successfully fetched ${data.length} email(s). Check console for details.`);
    return data;
};

export const generateChatTitle = async (chatId: string): Promise<{ title: string }> => {
    const token = await getAuthToken();
    if (!token) {
        throw new Error('Authentication token not found.');
    }

    const response = await fetch(`${API_BASE_URL}/chat/${chatId}/generate-title`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate chat title.');
    }

    return response.json();
};

export const countTokens = async (chatId: string): Promise<{ chat_id: string; current_context_tokens: number; max_context_tokens: number }> => {
    const token = await getAuthToken();
    if (!token) {
        throw new Error('Authentication token not found.');
    }

    const response = await fetch(`${API_BASE_URL}/chat/${chatId}/count-tokens`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to count tokens.');
    }

    return response.json();
};

export const streamAgentTest = async (
    agentConfig: any,
    message: any,
    chatHistory: any[],
    userTimezone: string,
    userTimestamp: string,
    knowledgeFileIds: string[],
    signal: AbortSignal,
    onMessage: (event: EventSourceMessage) => void,
    onError: (error: any) => void,
    onClose: () => void
) => {
    const token = await getAuthToken();
    if (!token) {
        onError(new Error("Authentication token not found. Please log in."));
        return;
    }

    try {
        await fetchEventSource(`${API_BASE_URL}/agents/test`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                agent_config: agentConfig,
                message: message,
                chat_history: chatHistory,
                user_timezone: userTimezone,
                user_timestamp: userTimestamp,
                knowledge_file_ids: knowledgeFileIds,
            }),
            signal: signal,
            onopen: async (response) => {
                if (!response.ok) {
                    const errorPayload = {
                        status: response.status,
                        statusText: response.statusText,
                        message: `Failed to connect to agent test stream. Server responded with status ${response.status}.`
                    };
                    onError(errorPayload);
                    throw new Error(JSON.stringify(errorPayload));
                }
            },
            onmessage: onMessage,
            onerror: (err) => {
                onError(err);
                // Throw so fetch-event-source stops retrying (it retries every
                // second forever when onerror returns normally).
                throw err;
            },
            onclose: onClose,
        });
    } catch {
        // onError already surfaced the failure; the throw above is how the
        // library is told to stop retrying.
    }
};

export const uploadFiles = async (files: File[]): Promise<{ files: { original_name: string; uri: string; size: number }[] }> => {
    const token = await getAuthToken();
    if (!token) {
        throw new Error('Authentication token not found.');
    }

    const formData = new FormData();
    files.forEach(file => {
        formData.append('files', file);
    });

    const response = await fetch(`${API_BASE_URL}/files/upload`, {
        method: 'POST',
        headers: {
            // 'Content-Type': 'multipart/form-data' is automatically set by the browser when using FormData
            'Authorization': `Bearer ${token}`,
        },
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload files.');
    }

    return response.json();
};

// --- Onboarding API ---
export const saveOnboarding = async (payload: { profile_summary?: string; voice_guide?: string; opinions?: string; }) => {
    const token = await getAuthToken();
    if (!token) {
        throw new Error('Authentication token not found.');
    }

    const response = await fetch(`${API_BASE_URL}/onboarding/save`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save onboarding details.');
    }

    return response.json();
};